import React, { useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  Clock,
  DotsVertical,
  Eye,
  Receipt,
  Send,
  ShoppingBag
} from 'tabler-icons-react';
import { ActionIcon, Badge, Button, Menu } from '@mantine/core';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {
  useBackPath,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { formatUtcDate } from '../../../helpers/format';
import MenuContent from '../../common/MenuContent';
import OrderProductsInfoCard from './OrderProductsInfoCard';
import OrderPaymentInfoCard from './OrderPaymentInfoCard';
import OrderCustomerInfoCard from './OrderCustomerInfoCard';
import OrderRegistrationsInfoCard from './OrderRegistrationsInfoCard';
import {
  REG_FORM_SUBMISSION_VIEW_ACTIONS,
  translateRegCartOrderData
} from '../../../helpers/registrationHelper';
import RegCartPaymentSummaryInfoCard from './RegCartPaymentSummaryInfoCard';
import EcomOrderTransactionModal from './EcomOrderTransactionModal';
import ResponsiveModal from '../../common/ResponsiveModal';
import RegFormSubmissionInfoCard from '../financials/RegFormSubmissionInfoCard';
import RegistrationListActionModals from '../financials/RegistrationListActionModals';
import MenuContentActionsList from '../../common/MenuContentActionsList';
import { Context as AuthContext } from '../../../providers/AuthProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import PageView from '../../common/PageView';
import RegCartOrderTransactionModal from './RegCartOrderTransactionModal';
import {
  REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM,
  REG_FORM_SUBMISSION_STATUS_ENUM
} from '../../../config/constants';
import OrderPaymentInstallmentDetailsModal from './OrderPaymentInstallmentDetailsModal';

const ORDER_ACTIONS_ENUM = {
  VIEW_SUBMISSION: 'VIEW_SUBMISSION',
  VIEW_SHOP_TRANSACTIONS: 'VIEW_SHOP_TRANSACTIONS',
  VIEW_All_TRANSACTIONS: 'VIEW_All_TRANSACTIONS',
  MANAGE_INSTALLMENTS: 'MANAGE_INSTALLMENTS'
};

const RegCartOrderDetailsView = ({ loading, regCart, baseUrl, onAction }) => {
  const { state: authState } = useContext(AuthContext);
  const { fetchRegCartOrder } = useContext(RegistrationAdminContext);
  const { isDesktopOrSmaller } = useMediaQueryIndex();
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const {
    state: selectModalState,
    onOpenModal: onOpenSelectModal,
    onCloseModal: onCloseSelectModal
  } = useModalState();
  const { search } = useLocation();
  const modalAction = new URLSearchParams(search).get('modal');

  const { backPath } = useBackPath('/admin/financials/registrations');

  const translatedOrder = translateRegCartOrderData(regCart);
  const {
    regFormSubmissions: regFormSubmissionData,
    ecomOrder: translatedEcomOrder,
    installments
  } = translatedOrder;
  const {
    ecomOrderProducts: ecomOrderProductData,
    isFullyRefunded: isEcomOrderFullyRefunded,
    isFullyCancelled: isEcomOrderFullyCancelled,
    isPendingFulfillment: isEcomOrderPendingFulfillment
  } = translatedEcomOrder;
  const regFormSubmissions = regFormSubmissionData.map(
    (d) => d.regFormSubmission
  );

  const isPendingPayment = regFormSubmissions.some(
    (s) =>
      s.fkRegFormSubmissionStatus?.toString() ===
      REG_FORM_SUBMISSION_STATUS_ENUM.PENDING_PAYMENT
  );

  useEffect(() => {
    if (modalAction && regCart) {
      switch (modalAction) {
        case 'installments':
          onOpenModal(ORDER_ACTIONS_ENUM.MANAGE_INSTALLMENTS);
          break;
        default:
          onOpenModal(modalAction);
          break;
      }
    }
  }, [modalAction, regCart]);

  const renderStatusBadges = () => (
    <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
      {regCart && (
        <>
          {regCart.cart.balance === 0 ? (
            <Badge color="blue" variant="filled">
              Paid
            </Badge>
          ) : isPendingPayment ? (
            <Badge color="blue" variant="filled">
              Pending Payment
            </Badge>
          ) : installments.length > 0 ? (
            <Badge color="blue" variant="filled">
              Payment Scheduled
            </Badge>
          ) : null}

          {regCart.cart.refundedAmountInCents > 0 &&
            (regCart.cart.refundedAmountInCents ===
            regCart.cart.totalAmountInCents ? (
              <Badge color="#c40000" variant="filled">
                Refunded
              </Badge>
            ) : (
              <Badge color="#c40000" variant="filled">
                Partially Refunded
              </Badge>
            ))}
        </>
      )}

      {installments.length > 0 && (
        <Badge color="grey" variant="filled">
          Installments
        </Badge>
      )}
    </AppFlexbox>
  );

  const onSelectFormSubmission = (regFormSubmission) => {
    switch (selectModalState.action) {
      default:
        onOpenModal(selectModalState.item?.action, [regFormSubmission]);
        break;
    }
    onCloseSelectModal();
  };

  return !loading && regCart ? (
    <PageView
      backPath={backPath}
      badgeContent={renderStatusBadges()}
      buttonAction={
        <MenuContent
          control={
            <Button
              color="dark"
              rightSection={<ChevronDown size={18} />}
              size="compact-md"
              style={{ fontSize: 14, flex: 1 }}
              variant="light"
            >
              Actions
            </Button>
          }
        >
          {/* <Menu.Item
            leftSection={<Download size={18} />}
            onClick={onExportOrder}
          >
            Export Order
          </Menu.Item> */}
          <Menu.Item
            leftSection={<Receipt size={18} />}
            onClick={() =>
              onOpenModal(ORDER_ACTIONS_ENUM.VIEW_All_TRANSACTIONS)
            }
          >
            View Transactions
          </Menu.Item>

          {installments.length > 0 && (
            <Menu.Item
              leftSection={<Clock size={18} />}
              onClick={() =>
                onOpenModal(ORDER_ACTIONS_ENUM.MANAGE_INSTALLMENTS)
              }
            >
              Installments
            </Menu.Item>
          )}

          <Menu.Item
            leftSection={<Send size={18} />}
            onClick={() =>
              onAction(REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.resendReceipt)
            }
          >
            Resend Receipt
          </Menu.Item>

          <Menu.Divider />

          <Menu.Label>Registrations</Menu.Label>

          <MenuContentActionsList
            actions={REG_FORM_SUBMISSION_VIEW_ACTIONS.filter(
              (a) =>
                !a.isVisable ||
                a.isVisable(regFormSubmissions, {
                  isAdmin: authState.userData.isAdmin,
                  isOrderDetails: true,
                  isSummaryView: true
                })
            )}
            onAction={(actionValue, action) => {
              if (regFormSubmissionData.length > 1) {
                onOpenSelectModal(null, {
                  title: action.modalTitle || action.label,
                  action: actionValue
                });
              }
              else {
                onOpenModal(actionValue, [
                  regFormSubmissionData[0].regFormSubmission
                ]);
              }
            }}
          />

          {/* <Menu.Divider />

          <Menu.Label>Order Actions</Menu.Label>
          <Menu.Item
            leftSection={<X color="#c40000" size={18} />}
            onClick={() => onOpenModal('cancel_order')}
            style={{ color: '#c40000' }}
          >
            Cancel Merch
          </Menu.Item>
          <Menu.Item
            component={Link}
            leftSection={<CreditCard color="#c40000" size={18} />}
            style={{ color: '#c40000' }}
            to={`/admin/financials/orders/${regCart.pkRegCart}/refund`}
          >
            Refund Order
          </Menu.Item> */}
        </MenuContent>
      }
      subTitle={`Placed
                ${dayjs(formatUtcDate(regCart.cart.purchaseDate)).format(
                  'MMMM D, YYYY'
                )}
                at
                ${dayjs(formatUtcDate(regCart.cart.purchaseDate)).format(
                  'h:mm A'
                )}`}
      title={`Order #${regCart.pkRegCart}`}
    >
      <AppFlexbox
        style={{ flexDirection: isDesktopOrSmaller ? 'column-reverse' : 'row' }}
      >
        <AppStack style={{ flex: 1 }}>
          <OrderRegistrationsInfoCard
            actionButton={
              regFormSubmissionData.length > 0 && (
                <MenuContent
                  control={
                    <ActionIcon color="dark" variant="subtle">
                      <DotsVertical />
                    </ActionIcon>
                  }
                >
                  <Menu.Item
                    component={Link}
                    leftSection={<Eye size={18} />}
                    onClick={(e) => {
                      if (regFormSubmissionData.length > 1) {
                        e.preventDefault();
                        onOpenSelectModal(null, {
                          title: 'View form submission details',
                          action: ORDER_ACTIONS_ENUM.VIEW_SUBMISSION
                        });
                      }
                    }}
                    to={`/admin/financials/registrations/${regFormSubmissionData[0].regFormSubmission.pkRegFormSubmission}`}
                  >
                    View Submission
                  </Menu.Item>

                  <MenuContentActionsList
                    actions={REG_FORM_SUBMISSION_VIEW_ACTIONS.filter(
                      (a) =>
                        !a.isVisable ||
                        a.isVisable(regFormSubmissions, {
                          isAdmin: authState.userData.isAdmin,
                          isOrderDetails: true
                        })
                    )}
                    onAction={(actionValue, action) => {
                      if (regFormSubmissionData.length > 1) {
                        onOpenSelectModal(null, {
                          title: action.modalTitle || action.label,
                          action: actionValue
                        });
                      }
                      else {
                        onOpenModal(actionValue, [
                          regFormSubmissionData[0].regFormSubmission
                        ]);
                      }
                    }}
                  />

                  {/* <Menu.Item
                    component={Link}
                    leftSection={<CreditCard color="#c40000" size={18} />}
                    style={{ color: '#c40000' }}
                    to={`/admin/financials/orders/${regCart.pkRegCart}/refund`}
                  >
                    Refund
                  </Menu.Item> */}
                </MenuContent>
              )
            }
            isCancelled={false}
            productData={regFormSubmissionData}
            showVendorName
            title="Registrations"
            urlBase="/admin/financials"
          />
          {ecomOrderProductData && ecomOrderProductData.length > 0 && (
            <OrderProductsInfoCard
              actionButton={
                <MenuContent
                  control={
                    <ActionIcon color="dark" variant="subtle">
                      <DotsVertical />
                    </ActionIcon>
                  }
                >
                  <Menu.Item
                    component={Link}
                    leftSection={<ShoppingBag size={18} />}
                    to={`/admin/financials/merchandise/${regCart.ecomOrder.pkEcomOrder}`}
                  >
                    View Order
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<Receipt size={18} />}
                    onClick={() => {
                      onOpenModal(ORDER_ACTIONS_ENUM.VIEW_SHOP_TRANSACTIONS);
                    }}
                  >
                    View Transactions
                  </Menu.Item>
                  {/* {(!isEcomOrderFullyRefunded ||
                    !isEcomOrderFullyCancelled) && (
                    <>
                      <Menu.Divider />
                      {!isEcomOrderFullyCancelled &&
                        isEcomOrderPendingFulfillment && (
                          <Menu.Item
                            leftSection={<X color="#c40000" size={18} />}
                            onClick={() => onOpenModal('cancel_order')}
                            style={{ color: '#c40000' }}
                          >
                            Cancel Merch
                          </Menu.Item>
                        )}
                      {!isEcomOrderFullyRefunded && (
                        <Menu.Item
                          component={Link}
                          leftSection={<CreditCard color="#c40000" size={18} />}
                          style={{ color: '#c40000' }}
                          to={`/admin/financials/orders/${regCart.pkRegCart}/refund`}
                        >
                          Refund
                        </Menu.Item>
                      )}
                    </>
                  )} */}
                </MenuContent>
              }
              isCancelled={isEcomOrderFullyCancelled}
              productData={ecomOrderProductData}
              productNavigationOverrideUrl={`/admin/financials/merchandise/${regCart.ecomOrder.pkEcomOrder}`}
              showVendorName
              title="Shop products"
            />
          )}

          <RegCartPaymentSummaryInfoCard
            actionMenu={
              <MenuContent
                control={
                  <ActionIcon color="dark" variant="subtle">
                    <DotsVertical />
                  </ActionIcon>
                }
              >
                <Menu.Item
                  leftSection={<Receipt size={18} />}
                  onClick={() => {
                    onOpenModal(ORDER_ACTIONS_ENUM.VIEW_All_TRANSACTIONS);
                  }}
                >
                  View Transactions
                </Menu.Item>

                {installments.length > 0 && (
                  <Menu.Item
                    leftSection={<Clock size={18} />}
                    onClick={() =>
                      onOpenModal(ORDER_ACTIONS_ENUM.MANAGE_INSTALLMENTS)
                    }
                  >
                    Installments
                  </Menu.Item>
                )}
              </MenuContent>
            }
            regCart={regCart}
            translatedOrder={translatedOrder}
          />
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isDesktopOrSmaller ? 'unset' : 350 }}
        >
          <OrderCustomerInfoCard
            billingAddress={
              regCart.ecomOrder?.ecomOrderPayments.find(
                (p) => !!p.ecomCustomerAddressBilling
              )?.ecomCustomerAddressBilling
            }
            customerEmail={regCart.user.email}
            customerName={regCart.user.name}
            shippingAddress={regCart.ecomOrder?.ecomCustomerAddressShipping}
          />
        </AppStack>
      </AppFlexbox>

      <ResponsiveModal
        isNewModalStyle
        isOpen={selectModalState.isOpen}
        onClose={onCloseSelectModal}
        title={selectModalState.item?.title || 'Select form submission'}
      >
        <AppStack style={{ gap: 16 }}>
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Please select a form submission to continue
          </AppText>
          {regFormSubmissionData
            .filter((f) =>
              selectModalState.item?.action ===
              REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.deny
                ? f.regFormSubmission.fkRegFormSubmissionStatus.toString() !==
                  REG_FORM_SUBMISSION_STATUS_ENUM.DENIED
                : selectModalState.item?.action ===
                  REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.reinstate
                ? f.regFormSubmission.fkRegFormSubmissionStatus.toString() ===
                  REG_FORM_SUBMISSION_STATUS_ENUM.DENIED
                : true
            )
            .map((s) => (
              <RegFormSubmissionInfoCard
                key={s.regFormSubmission.pkRegFormSubmission}
                navigateUrl={
                  selectModalState.item?.action ===
                  ORDER_ACTIONS_ENUM.VIEW_SUBMISSION
                    ? `/admin/financials/registrations/${s.regFormSubmission.pkRegFormSubmission}`
                    : null
                }
                onSelect={onSelectFormSubmission}
                regFormSubmission={s.regFormSubmission}
              />
            ))}
        </AppStack>
      </ResponsiveModal>

      <RegistrationListActionModals
        modalState={modalState}
        onActionComplete={() => {
          fetchRegCartOrder({ pkRegCart: regCart.pkRegCart });
          onCloseModal();
        }}
        onCloseModal={onCloseModal}
      />

      <RegCartOrderTransactionModal
        isOpen={
          modalState.isOpen &&
          modalState.action === ORDER_ACTIONS_ENUM.VIEW_All_TRANSACTIONS
        }
        onClose={onCloseModal}
        regCartOrder={regCart}
        translatedOrder={translatedOrder}
      />

      {regCart.ecomOrder && (
        <EcomOrderTransactionModal
          ecomOrder={regCart.ecomOrder}
          isOpen={
            modalState.isOpen &&
            modalState.action === ORDER_ACTIONS_ENUM.VIEW_SHOP_TRANSACTIONS
          }
          onClose={onCloseModal}
        />
      )}

      <OrderPaymentInstallmentDetailsModal
        isOpen={
          modalState.isOpen &&
          modalState.action === ORDER_ACTIONS_ENUM.MANAGE_INSTALLMENTS
        }
        onClose={onCloseModal}
        onRefreshData={() =>
          fetchRegCartOrder({ pkRegCart: regCart.pkRegCart, skipLoading: true })
        }
        pkRegCart={regCart.pkRegCart}
        regCartBalance={regCart.cart}
      />
    </PageView>
  ) : (
    <PageView loading>
      <AppFlexbox
        style={{ flexDirection: isDesktopOrSmaller ? 'column-reverse' : 'row' }}
      >
        <AppStack style={{ flex: 1 }}>
          <OrderProductsInfoCard loading />
          <OrderPaymentInfoCard loading />
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isDesktopOrSmaller ? 'unset' : 350 }}
        >
          <OrderCustomerInfoCard loading />
        </AppStack>
      </AppFlexbox>
    </PageView>
  );
};

RegCartOrderDetailsView.propTypes = {
  baseUrl: PropTypes.string,
  loading: PropTypes.bool,
  onAction: PropTypes.func,
  regCart: PropTypes.object
};

export default RegCartOrderDetailsView;
